import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { PublicSettings } from '@ili/shared';
import { get } from './api';
import { safeStorage } from './device';
import { applyAccent } from './theme';

const KEY = 'ili.settings';
const store = safeStorage();
const cached = (): PublicSettings | undefined => {
  try {
    const raw = store.get(KEY);
    return raw ? (JSON.parse(raw) as PublicSettings) : undefined;
  } catch {
    return undefined;
  }
};

const Ctx = createContext<PublicSettings | null>(null);

/** Loads LGU settings (name, timezone, barangays, emergency mode…). Cached locally so the app opens offline. */
export function SettingsProvider({ children, fallback }: { children: ReactNode; fallback: (state: 'loading' | 'error') => ReactNode }) {
  const q = useQuery({
    queryKey: ['lgu'],
    queryFn: () => get<PublicSettings>('/lgu'),
    initialData: cached,
    initialDataUpdatedAt: 0,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
  useEffect(() => {
    if (!q.data) return;
    store.set(KEY, JSON.stringify(q.data));
    applyAccent(q.data.theme.accent);
  }, [q.data]);
  // First visit with no signal: React Query pauses instead of failing, so show the offline screen (with Call 911).
  if (!q.data) return <>{fallback(q.isError || q.fetchStatus === 'paused' ? 'error' : 'loading')}</>;
  return <Ctx.Provider value={q.data}>{children}</Ctx.Provider>;
}

export function useSettings(): PublicSettings {
  const s = useContext(Ctx);
  if (!s) throw new Error('useSettings outside SettingsProvider');
  return s;
}

export function useBarangayName() {
  const { barangays } = useSettings();
  return (id: number | null | undefined) => barangays.find((b) => b.id === id)?.name ?? null;
}

/** Sets document.title to "<page> · <LGU display name>". */
export function useDocumentTitle(title?: string | null) {
  const { displayName } = useSettings();
  useEffect(() => {
    document.title = title ? `${title} · ${displayName}` : displayName;
  }, [title, displayName]);
}
